import { Box, Button, IconButton, MenuItem, TextField } from '@mui/material';
import React, { ChangeEvent } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';

interface ServiceRow {
  recurso: string;
  qtde: string;
  meses12: string;
  meses24: string;
  meses36: string;
  meses48: string;
  meses60: string;
}

interface FormsProposalProps {
  formData: {
    notes: string;
    implementationFee: string;
  };
  onChange: (data: { notes?: string; implementationFee?: string }) => void;
  goToNextPage: () => void;
  servicesRows: ServiceRow[];
  setServicesRows: React.Dispatch<React.SetStateAction<ServiceRow[]>>;
  formType: 'completo' | 'simples';
  setFormType: React.Dispatch<React.SetStateAction<'completo' | 'simples'>>;
}

const FormsProposalFifth: React.FC<FormsProposalProps> = ({
  formData,
  onChange,
  goToNextPage,
  servicesRows,
  setServicesRows,
  formType,
  setFormType,
}) => {
  const [serviceForm, setServiceForm] = React.useState<ServiceRow>({
    recurso: '',
    qtde: '',
    meses12: '',
    meses24: '',
    meses36: '',
    meses48: '',
    meses60: '',
  });
  const [editIndex, setEditIndex] = React.useState<number | null>(null);

  function handleServiceChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setServiceForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    const { name, value } = event.target;
    onChange({ [name]: value });
  }

  function handleAddOrUpdateService() {
    if (!serviceForm.recurso) return;

    if (editIndex !== null) {
      const updatedRows = [...servicesRows];
      updatedRows[editIndex] = serviceForm;
      setServicesRows(updatedRows);
      setEditIndex(null);
    } else {
      setServicesRows((prev) => [...prev, serviceForm]);
    }

    setServiceForm({
      recurso: '',
      qtde: '',
      meses12: '',
      meses24: '',
      meses36: '',
      meses48: '',
      meses60: '',
    });
  }

  function handleEdit(index: number) {
    setServiceForm(servicesRows[index]);
    setEditIndex(index);
  }

  function handleDelete(index: number) {
    const updated = servicesRows.filter((_, i) => i !== index);
    setServicesRows(updated);
    if (editIndex === index) {
      setServiceForm({
        recurso: '',
        qtde: '',
        meses12: '',
        meses24: '',
        meses36: '',
        meses48: '',
        meses60: '',
      });
      setEditIndex(null);
    }
  }

  React.useEffect(() => {
    if (formType === 'simples' && servicesRows.length === 0) {
      setServicesRows([
        {
          recurso:
            'Gestão de Acesso dos alunos através de reconhecimento facial',
          qtde: '',
          meses12: '',
          meses24: '',
          meses36: '',
          meses48: '',
          meses60: '',
        },
        {
          recurso: 'Painel de Conferencia dos responsáveis',
          qtde: '',
          meses12: '',
          meses24: '',
          meses36: '',
          meses48: '',
          meses60: '',
        },
        {
          recurso: 'Notificação via API, Aplicativo da escola e Telegram',
          qtde: '',
          meses12: '',
          meses24: '',
          meses36: '',
          meses48: '',
          meses60: '',
        },
        {
          recurso: 'Gestão de Relatórios',
          qtde: '',
          meses12: '',
          meses24: '',
          meses36: '',
          meses48: '',
          meses60: '',
        },
        {
          recurso: 'Integração com sistema de alarme',
          qtde: '',
          meses12: '',
          meses24: '',
          meses36: '',
          meses48: '',
          meses60: '',
        },
        {
          recurso: 'Valor Total',
          qtde: '',
          meses12: '',
          meses24: '',
          meses36: '',
          meses48: '',
          meses60: '',
        },
        {
          recurso: 'Aplicativo IOS & Android por aluno',
          qtde: '',
          meses12: '',
          meses24: '',
          meses36: '',
          meses48: '',
          meses60: '',
        },
      ]);
    }
  }, [formType, setServicesRows, servicesRows]);

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: 3,
        backgroundColor: 'var(--app-surface-2)',
        width: '100%',
        height: '100%',
        boxShadow: '0 4px 24px rgba(24, 19, 19, 0.25)',
        padding: 3,
        gap: 2,
      }}
    >
      <TextField
        select
        label="Tipo de formulário"
        value={formType}
        onChange={(e) => setFormType(e.target.value as 'completo' | 'simples')}
        size="small"
        sx={{ width: '100%' }}
      >
        <MenuItem value="completo">Software + Hardware</MenuItem>
        <MenuItem value="simples">Software</MenuItem>
      </TextField>

      {formType === 'completo' ? (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%' }}>
          {[
            'recurso',
            'qtde',
            'meses12',
            'meses24',
            'meses36',
            'meses48',
            'meses60',
          ].map((field) => (
            <TextField
              key={field}
              label={field === 'qtde' ? 'Qtde' : field}
              name={field}
              value={serviceForm[field as keyof ServiceRow]}
              onChange={handleServiceChange}
              size="small"
              sx={{ width: field === 'recurso' ? 150 : 90 }}
            />
          ))}
          <Button variant="contained" onClick={handleAddOrUpdateService}>
            {editIndex !== null ? <CheckIcon /> : '+'}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <TextField
            label="Descrição do Serviço"
            name="recurso"
            value={serviceForm.recurso}
            onChange={handleServiceChange}
            size="small"
            sx={{ width: '60%' }}
          />
          <TextField
            label="Valor"
            name="qtde"
            value={serviceForm.qtde}
            onChange={handleServiceChange}
            size="small"
            sx={{ width: '40%' }}
          />
          <Button variant="contained" onClick={handleAddOrUpdateService}>
            {editIndex !== null ? <CheckIcon /> : '+'}
          </Button>
        </Box>
      )}

      <Box sx={{ width: '100%', mt: 2, maxHeight: 300, overflowY: 'auto' }}>
        {servicesRows.map((row, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              border: 'white',
              padding: 1,
              borderRadius: 1,
              marginBottom: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>{row.recurso}</Box>
            <Box sx={{ flex: 1 }}>{row.qtde}</Box>
            {formType === 'completo' && (
              <>
                <Box sx={{ width: 50 }}>{row.meses12}</Box>
                <Box sx={{ width: 50 }}>{row.meses24}</Box>
                <Box sx={{ width: 50 }}>{row.meses36}</Box>
                <Box sx={{ width: 50 }}>{row.meses48}</Box>
                <Box sx={{ width: 50 }}>{row.meses60}</Box>
              </>
            )}
            <IconButton onClick={() => handleEdit(index)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => handleDelete(index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>

      <TextField
        label="Taxa de Implementação"
        name="implementationFee"
        value={formData.implementationFee}
        onChange={handleChange}
        size="small"
        sx={{ width: '100%' }}
      />

      <Button variant="contained" onClick={goToNextPage}>
        Salvar
      </Button>
    </Box>
  );
};

export default FormsProposalFifth;
